import React, { useCallback, useMemo, useState, useEffect } from 'react';
import DataRoomNdaTableContext from './DataRoomNdaTableProvider.context';
import {
  DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION,
  DataRoomNdaSigner,
  DataRoomNdaSigning,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import DataRoomNdaSignerDrawer from '../../components/DataRoomNdaSignerDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getSignerInfo } from '../../components/DataRoomNdasTable/components';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import DataRoomChooseNdaModal from '../../components/DataRoomChooseNdaModal';
import useDrawerNavigation from 'hooks/useDrawerNavigation';
import { getSignerNdaSigning } from '../../utils';
import useSelect from 'hooks/useSelect';

type DataRoomNdaTableProviderProps = {
  signers: DataRoomNdaSigner[];
  totalPages?: number;
  isLoading?: boolean;
  updateSigner: (signer: Partial<DataRoomNdaSigner>) => void;
  refetch: () => void;
};

const DataRoomNdaTableProvider: React.FC<DataRoomNdaTableProviderProps> = (
  props,
) => {
  const { children, signers, totalPages, isLoading, updateSigner, refetch } =
    props;

  const [selectedSigner, setSelectedSigner] = useState<DataRoomNdaSigner>(null);
  const [selectedSigning, setSelectedSigning] =
    useState<DataRoomNdaSigning>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { selected, isAllSelected, selectAll, removeAll, updateSelected } =
    useSelect<DataRoomNdaSigner>(signers);

  const openNdaDrawer = useCallback(
    (signer: DataRoomNdaSigner, signing: DataRoomNdaSigning) => {
      setSelectedSigner(signer);
      setSelectedSigning(signing);
      drawer?.setOpen(true);
    },
    [drawer],
  );

  const selectedNdaId = useMemo(
    () => selectedSigning?.dataRoomNdaId,
    [selectedSigning],
  );

  const navigateToSigner = useCallback(
    (signer: DataRoomNdaSigner) =>
      openNdaDrawer(signer, getSignerNdaSigning(signer, selectedNdaId)),
    [openNdaDrawer, selectedNdaId],
  );

  const getHasSelectedNda = useCallback(
    (signer: DataRoomNdaSigner) => !!getSignerNdaSigning(signer, selectedNdaId),
    [selectedNdaId],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: signers,
    selectedId: selectedSigner?._id,
    totalPages,
    isLoading,
    getIsNavigable: getHasSelectedNda,
    onNavigate: navigateToSigner,
  });

  const openNdaModal = useCallback(
    (signer: DataRoomNdaSigner) => {
      setSelectedSigner(signer);
      modal?.open();
    },
    [modal],
  );

  const updateSelectedSigner = useCallback(
    (signer: Partial<DataRoomNdaSigner>) => {
      updateSigner(signer);

      if (selectedSigner?._id !== signer._id) return;

      setSelectedSigner((prev) => ({ ...prev, ...signer }));
      setSelectedSigning((prev) =>
        signer.dataRoomNdaSignings.find(({ _id }) => _id === prev._id),
      );
    },
    [selectedSigner?._id, updateSigner],
  );

  const onSendReminder = useCallback(
    (signingId: string) => {
      updateSelectedSigner({
        ...selectedSigner,
        dataRoomNdaSignings: selectedSigner.dataRoomNdaSignings.map((signing) =>
          signing._id !== signingId
            ? signing
            : {
                ...signing,
                sentDate: new Date(),
                timeline: [
                  ...signing.timeline,
                  {
                    action: DATA_ROOM_NDA_SIGNING_TIMELINE_ACTION.REMINDER,
                    date: new Date(),
                    ...getSignerInfo(selectedSigner),
                  },
                ],
              },
        ),
      });
    },
    [selectedSigner, updateSelectedSigner],
  );

  const onDrawerClose = useCallback(() => {
    setSelectedSigner(null);
    setSelectedSigning(null);
  }, []);

  const onSigningSelect = useCallback(
    (signingId: string) => {
      if (!selectedSigner) return;

      const singing = selectedSigner.dataRoomNdaSignings.find(
        ({ _id }) => _id === signingId,
      );

      setSelectedSigning(singing);
      modal.close();
      drawer?.setOpen(true);
    },
    [selectedSigner, drawer, modal],
  );

  useEffect(() => {
    removeAll();
  }, [signers, removeAll]);

  return (
    <DataRoomNdaTableContext.Provider
      value={{
        selected,
        isAllSelected,
        openNdaDrawer,
        openNdaModal,
        selectAll,
        removeAll,
        updateSelected,
        refetch,
      }}
    >
      {children}
      <DataRoomNdaSignerDrawer
        ref={drawerRef}
        dataRoomSigner={selectedSigner}
        dataRoomNdaSigning={selectedSigning}
        closeAction={onDrawerClose}
        onSendReminder={onSendReminder}
        onPrev={onPrev}
        onNext={onNext}
      />
      <DataRoomChooseNdaModal
        ref={modalRef}
        signings={selectedSigner?.dataRoomNdaSignings}
        onSigningSelect={onSigningSelect}
      />
    </DataRoomNdaTableContext.Provider>
  );
};

export default DataRoomNdaTableProvider;
